import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from "../../Services/Firebase/FirebaseIndex";
import BandCrowd from "../../assets/Images/BandCrowd.jpg";
import gigifyLogo from "../../assets/Logo/GIGiFY_Logo_Vertical_with_tagline.png";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    setError('');
    setMessage('');
    setIsLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setMessage('Password reset email sent! Please check your inbox and follow the instructions to reset your password.');
    } catch (error) {
      let errorMessage = "Failed to send password reset email";
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = "No account found with this email address";
          break;
        case 'auth/invalid-email':
          errorMessage = "Invalid email format";
          break;
        case 'auth/too-many-requests':
          errorMessage = "Too many requests. Please try again later";
          break;
        default:
          errorMessage = error.message || "Failed to send password reset email";
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [email]);

  const handleInputChange = useCallback((e) => {
    setEmail(e.target.value);
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      backgroundImage: `url(${BandCrowd})`,
      backgroundSize: 'cover',
      backgroundPosition: 'center',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem'
    }}>
      <Link to="/" style={{
        marginBottom: '3rem',
        transition: 'transform 0.3s ease'
      }}
        onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
        onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
      >
        <img 
          src={gigifyLogo} 
          alt="GIGiFY"
          style={{
            width: '300px',
            height: 'auto'
          }}
        />
      </Link>

      <div style={{
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        padding: '3rem',
        borderRadius: '15px',
        textAlign: 'center',
        maxWidth: '500px',
        width: '100%',
        boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)'
      }}>
        <h1 style={{
          color: '#144874',
          fontSize: '2rem',
          marginBottom: '1rem',
          fontFamily: 'Pondar, sans-serif',
          textAlign: 'center'
        }}>Forgot Password?</h1>
        
        <p style={{
          color: '#666',
          marginBottom: '2rem',
          fontSize: '1.1rem',
          lineHeight: '1.6',
          fontFamily: 'Montserrat, sans-serif'
        }}>
          No worries! Enter your email address and we'll send you a link to reset your password.
        </p>

        {message && (
          <div style={{
            backgroundColor: '#d4edda',
            color: '#155724',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
            fontFamily: 'Montserrat, sans-serif'
          }}>
            {message}
          </div>
        )}

        {error && (
          <div style={{
            backgroundColor: '#f8d7da',
            color: '#721c24',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            fontSize: '0.9rem',
            fontFamily: 'Montserrat, sans-serif'
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1.5rem',
          marginBottom: '2rem'
        }}>
          <input
            type="email"
            placeholder="Enter your email address"
            value={email}
            onChange={handleInputChange}
            required
            style={{
              padding: '1rem',
              borderRadius: '8px',
              border: '2px solid #dadee6',
              fontSize: '1rem',
              fontFamily: 'Montserrat, sans-serif',
              transition: 'border-color 0.3s ease',
              backgroundColor: 'white'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#01a2f1';
              e.target.style.boxShadow = '0 0 0 3px rgba(1, 162, 241, 0.1)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#dadee6';
              e.target.style.boxShadow = 'none';
            }}
          />
          
          <button
            type="submit"
            disabled={isLoading}
            style={{
              backgroundColor: '#f85008',
              color: 'white',
              padding: '1rem 2rem',
              borderRadius: '25px',
              border: 'none',
              fontSize: '1.1rem',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontFamily: 'Montserrat, sans-serif',
              transition: 'all 0.3s ease',
              opacity: isLoading ? 0.7 : 1
            }}
            onMouseEnter={e => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#d94400';
                e.target.style.transform = 'scale(1.05)';
                e.target.style.boxShadow = '0 4px 12px rgba(248, 80, 8, 0.3)';
              }
            }}
            onMouseLeave={e => {
              if (!isLoading) {
                e.target.style.backgroundColor = '#f85008';
                e.target.style.transform = 'scale(1)';
                e.target.style.boxShadow = 'none';
              }
            }}
          >
            {isLoading ? 'Sending...' : 'Send Reset Link'}
          </button>
        </form>

        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
          <Link
            to="/login"
            style={{
              backgroundColor: 'transparent',
              color: '#01a2f1',
              padding: '0.75rem 1.5rem',
              borderRadius: '25px',
              textDecoration: 'none',
              display: 'inline-block',
              fontSize: '1rem',
              fontFamily: 'Montserrat, sans-serif',
              border: '2px solid #01a2f1',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={e => {
              e.target.style.backgroundColor = '#01a2f1';
              e.target.style.color = 'white';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={e => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#01a2f1';
              e.target.style.transform = 'scale(1)';
            }}
          >
            Back to Login
          </Link>

          <Link
            to="/register"
            style={{
              backgroundColor: 'transparent',
              color: '#144874',
              padding: '0.75rem 1.5rem',
              borderRadius: '25px',
              textDecoration: 'none',
              display: 'inline-block',
              fontSize: '1rem',
              fontFamily: 'Montserrat, sans-serif',
              border: '2px solid #144874',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={e => {
              e.target.style.backgroundColor = '#144874';
              e.target.style.color = 'white';
              e.target.style.transform = 'scale(1.05)';
            }}
            onMouseLeave={e => {
              e.target.style.backgroundColor = 'transparent';
              e.target.style.color = '#144874';
              e.target.style.transform = 'scale(1)';
            }}
          >
            Create Account
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;